import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Sparkles, ArrowUpRight } from 'lucide-react'
import { api } from '@/lib/api'
import { track } from '@/lib/analytics'

interface Suggestion {
  articleId: string
  title: string
  subtitle: string
  slug: string
  score: number
  reason: string
}

export function SuggestedArticles({ subject, body }: { subject: string; body: string }) {
  const query = `${subject}\n${body}`.trim()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (query.length < 12) {
      setSuggestions([])
      return
    }
    const handle = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await api<{ data: Suggestion[] }>('/kb-articles/suggest', {
          method: 'POST',
          body: { query, limit: 3 },
        })
        setSuggestions(res.data)
      } catch {
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 800)
    return () => clearTimeout(handle)
  }, [query])

  if (!loading && suggestions.length === 0) return null

  return (
    <div className="rounded border border-border bg-muted/40 p-3 text-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5" />
        {loading ? 'Looking for related articles…' : 'Articles that may help'}
      </div>
      {suggestions.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {suggestions.map((s, idx) => (
            <li key={s.articleId}>
              <Link
                to={`/kb/${s.slug}` as never}
                target="_blank"
                rel="noreferrer"
                onClick={() => track('kb_suggestion_clicked', {
                  article_id: s.articleId,
                  slug: s.slug,
                  position: idx,
                  score: s.score,
                  query_length: query.length
                })}
                className="group flex items-start gap-2 rounded px-2 py-1.5 hover:bg-background"
              >
                <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium text-foreground">{s.title}</div>
                  <div className="text-xs text-muted-foreground">{s.reason}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
